import React, { useEffect, useState } from "react";
import { Plus, Edit, Trash2, FileText, Calendar, User, Clock, Paperclip, ExternalLink, Eye } from "lucide-react";
import Modal from "../components/Common/Modal";
import { TreatmentHistory } from "../types";
import {
  getTreatmentHistoryByPatientId,
  createTreatmentHistory,
  updateTreatmentHistory,
  deleteTreatmentHistory,
  viewTreatmentHistoryDocument,
} from "../services/treatmentHistoryService";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "react-toastify";

interface TreatmentHistoryTabProps {
  patientId: string;
  onDataChanged?: () => void;
  readOnly?: boolean;
}

const TreatmentHistoryTab: React.FC<TreatmentHistoryTabProps> = ({
  patientId,
  onDataChanged,
  readOnly = false,
}) => {
  const { user } = useAuth();

  const [records, setRecords] = useState<TreatmentHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [mode, setMode] = useState<"add" | "edit" | null>(null);
  const [selectedRecord, setSelectedRecord] = useState<TreatmentHistory | null>(null);
  const [detailRecord, setDetailRecord] = useState<TreatmentHistory | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  // Form states
  const [eventDate, setEventDate] = useState("");
  const [timingOfRelapse, setTimingOfRelapse] = useState("");
  const [diseaseStatus, setDiseaseStatus] = useState("");
  const [previousOccurrence, setPreviousOccurrence] = useState("");
  const [performanceStatus, setPerformanceStatus] = useState("");
  const [comorbidities, setComorbidities] = useState("");
  const [treatmentProgression, setTreatmentProgression] = useState("");
  const [comments, setComments] = useState("");
  const [documentFile, setDocumentFile] = useState<File | null>(null);
  const [existingDocName, setExistingDocName] = useState<string | null>(null);

  const handleEventDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, "");
    if (value.length > 2) {
      value = value.slice(0, 2) + "-" + value.slice(2);
    }
    if (value.length > 5) {
      value = value.slice(0, 5) + "-" + value.slice(5);
    }
    value = value.slice(0, 10);
    setEventDate(value);
  };

  const loadData = async () => {
    if (!patientId) return;
    try {
      setLoading(true);
      const data = await getTreatmentHistoryByPatientId(patientId);
      setRecords(data);
    } catch (err: any) {
      console.error("Failed to load treatment history:", err);
      toast.error("Failed to load treatment history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [patientId]);

  const resetForm = () => {
    setEventDate("");
    setTimingOfRelapse("");
    setDiseaseStatus("");
    setPreviousOccurrence("");
    setPerformanceStatus("");
    setComorbidities("");
    setTreatmentProgression("");
    setComments("");
    setDocumentFile(null);
    setExistingDocName(null);
    setSelectedRecord(null);
  };

  const openAddModal = () => {
    resetForm();
    setMode("add");
  };

  const openEditModal = (record: TreatmentHistory) => {
    setSelectedRecord(record);
    setEventDate(record.event_date || "");
    setTimingOfRelapse(record.timing_of_relapse || "");
    setDiseaseStatus(record.disease_status || "");
    setPreviousOccurrence(record.previous_occurrence || "");
    setPerformanceStatus(record.performance_status || "");
    setComorbidities(record.comorbidities || "");
    setTreatmentProgression(record.treatment_progression || "");
    setComments(record.comments || "");
    setExistingDocName(record.document_name || null);
    setDocumentFile(null);
    setMode("edit");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setSaving(true);
      const formData = new FormData();
      formData.append("patient_id", patientId);
      if (eventDate) formData.append("event_date", eventDate);
      if (timingOfRelapse) formData.append("timing_of_relapse", timingOfRelapse);
      if (diseaseStatus) formData.append("disease_status", diseaseStatus);
      if (previousOccurrence) formData.append("previous_occurrence", previousOccurrence);
      if (performanceStatus) formData.append("performance_status", performanceStatus);
      if (comorbidities) formData.append("comorbidities", comorbidities);
      if (treatmentProgression) formData.append("treatment_progression", treatmentProgression);
      if (comments) formData.append("comments", comments);

      if (documentFile) {
        formData.append("file", documentFile);
      }

      if (mode === "add") {
        const newRecord = await createTreatmentHistory(formData);
        toast.success("Treatment history record added successfully");
        setRecords((prev) => [newRecord, ...prev]);
      } else if (mode === "edit" && selectedRecord) {
        const updatedRecord = await updateTreatmentHistory(selectedRecord.id, formData);
        toast.success("Treatment history record updated successfully");
        setRecords((prev) =>
          prev.map((r) => (r.id === updatedRecord.id ? updatedRecord : r))
        );
      }

      if (onDataChanged) onDataChanged();
      setMode(null);
      resetForm();
    } catch (err: any) {
      console.error("Failed to save treatment history:", err);
      toast.error(err?.response?.data?.message || "Failed to save treatment history");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedRecord) return;
    try {
      await deleteTreatmentHistory(selectedRecord.id);
      toast.success("Record deleted successfully");
      setRecords((prev) => prev.filter((r) => r.id !== selectedRecord.id));
      setIsDeleteOpen(false);
      setSelectedRecord(null);
      if (onDataChanged) onDataChanged();
    } catch (err: any) {
      console.error("Failed to delete record:", err);
      toast.error("Failed to delete record");
    }
  };

  const handleViewDoc = async (id: number) => {
    try {
      const url = await viewTreatmentHistoryDocument(id);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err: any) {
      toast.error(err.message || "Failed to open document");
    }
  };

  const formatFileSize = (bytes?: number | null): string => {
    if (!bytes) return "";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Treatment & Disease History</h3>
          <p className="text-sm text-gray-500">
            Maintain historical clinical records, relapses, performance status, and comorbidities for MDT evaluation.
          </p>
        </div>

        {!readOnly && (
          <button onClick={openAddModal} className="btn-primary flex items-center space-x-2 w-full sm:w-auto justify-center flex-shrink-0">
            <Plus className="h-4 w-4" />
            <span>Add Treatment History</span>
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      ) : records.length === 0 ? (
        <div className="card text-center py-12">
          <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h4 className="text-lg font-medium text-gray-900 mb-1">No Treatment History Records</h4>
          <p className="text-sm text-gray-500 mb-4">
            No previous treatment or relapse history records have been added for this patient yet.
          </p>
          {!readOnly && (
            <button onClick={openAddModal} className="btn-primary inline-flex items-center space-x-2">
              <Plus className="h-4 w-4" />
              <span>Add First Record</span>
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-gray-600">
              <thead className="bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-700 uppercase tracking-wider">
                <tr>
                  <th className="py-3 px-4">Event / Record Date</th>
                  <th className="py-3 px-4">Timing of Relapse</th>
                  <th className="py-3 px-4">Disease Status</th>
                  <th className="py-3 px-4">Performance Status</th>
                  <th className="py-3 px-4">Document</th>
                  <th className="py-3 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {records.map((record, index) => (
                  <tr key={record.id} className="hover:bg-gray-50/80 transition-colors">
                    <td className="py-3 px-4 whitespace-nowrap text-gray-700 font-medium">
                      {record.event_date || new Date(record.createdAt).toLocaleDateString()}
                    </td>
                    <td className="py-3 px-4 max-w-xs truncate text-gray-900 font-medium">
                      {record.timing_of_relapse || "-"}
                    </td>
                    <td className="py-3 px-4 max-w-xs truncate text-gray-800">
                      {record.disease_status || "-"}
                    </td>
                    <td className="py-3 px-4 max-w-xs truncate text-gray-800">
                      {record.performance_status || "-"}
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap">
                      {record.document_url ? (
                        <button
                          type="button"
                          onClick={() => handleViewDoc(record.id)}
                          className="inline-flex items-center text-xs text-primary-600 hover:text-primary-800 font-medium space-x-1"
                        >
                          <Paperclip className="h-3.5 w-3.5" />
                          <span className="truncate max-w-[120px]">{record.document_name || "Attachment"}</span>
                        </button>
                      ) : (
                        <span className="text-gray-400 text-xs">None</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-right whitespace-nowrap">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => {
                            setDetailRecord(record);
                            setIsDetailOpen(true);
                          }}
                          className="inline-flex items-center space-x-1 px-2.5 py-1 text-xs font-medium text-primary-700 bg-primary-50 hover:bg-primary-100 rounded border border-primary-200 transition-colors"
                          title="View Full Details"
                        >
                          <Eye className="h-3.5 w-3.5" />
                          <span>View Details</span>
                        </button>
                        {!readOnly && (
                          <>
                            <button
                              title="Edit Record"
                              onClick={() => openEditModal(record)}
                              className="p-1.5 text-gray-500 hover:text-primary-600 rounded hover:bg-gray-100 transition-colors"
                            >
                              <Edit className="h-4 w-4 text-gray-500" />
                            </button>
                            <button
                              title="Delete Record"
                              onClick={() => {
                                setSelectedRecord(record);
                                setIsDeleteOpen(true);
                              }}
                              className="p-1.5 text-gray-500 hover:text-red-600 rounded hover:bg-gray-100 transition-colors"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ADD / EDIT MODAL */}
      <Modal
        title={mode === "edit" ? "Edit Treatment History Record" : "Add Treatment History Record"}
        isOpen={mode !== null}
        onClose={() => setMode(null)}
        widthClassName="max-w-3xl"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Timing of Relapse
              </label>
              <input
                type="text"
                value={timingOfRelapse}
                onChange={(e) => setTimingOfRelapse(e.target.value)}
                placeholder="e.g., Relapse after 12 months, Early Relapse"
                className="input-field mt-1"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Disease Status
              </label>
              <input
                type="text"
                value={diseaseStatus}
                onChange={(e) => setDiseaseStatus(e.target.value)}
                placeholder="e.g., Complete Remission, Relapsed/Refractory, Progressive Disease"
                className="input-field mt-1"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Performance Status
              </label>
              <input
                type="text"
                value={performanceStatus}
                onChange={(e) => setPerformanceStatus(e.target.value)}
                placeholder="e.g., ECOG 1, Karnofsky 80%"
                className="input-field mt-1"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Event Date / Year
              </label>
              <input
                type="text"
                value={eventDate}
                onChange={handleEventDateChange}
                placeholder="DD-MM-YYYY"
                maxLength={10}
                className="input-field mt-1"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Previous Occurrence
            </label>
            <textarea
              value={previousOccurrence}
              onChange={(e) => setPreviousOccurrence(e.target.value)}
              placeholder="Describe previous occurrences or lines of therapy..."
              rows={2}
              className="input-field mt-1"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Comorbidities
            </label>
            <textarea
              value={comorbidities}
              onChange={(e) => setComorbidities(e.target.value)}
              placeholder="List patient comorbidities..."
              rows={2}
              className="input-field mt-1"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Treatment Progression / Context
            </label>
            <textarea
              value={treatmentProgression}
              onChange={(e) => setTreatmentProgression(e.target.value)}
              placeholder="Details on treatment regimens, progression, response to therapy..."
              rows={3}
              className="input-field mt-1"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Comments
            </label>
            <textarea
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              placeholder="Additional clinical comments..."
              rows={2}
              className="input-field mt-1"
            />
          </div>

          {/* Document Upload */}
          <div className="border-t border-gray-200 pt-3">
            <label className="block text-sm font-medium text-gray-700">
              Upload Document <span className="text-gray-400 font-normal">(Supporting types: PDF, DOC, DOCX, TXT)</span>
            </label>
            <input
              type="file"
              accept=".pdf,.doc,.docx,.txt"
              onChange={(e) => {
                const file = e.target.files?.[0] || null;
                setDocumentFile(file);
              }}
              className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
            />
            {existingDocName && !documentFile && (
              <p className="text-xs text-gray-500 mt-1">
                Current Document: <span className="font-medium text-gray-700">{existingDocName}</span>
              </p>
            )}
          </div>

          <div className="flex justify-end space-x-3 border-t border-gray-200 pt-4">
            <button
              type="button"
              onClick={() => setMode(null)}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving
                ? "Saving..."
                : mode === "edit"
                ? "Update Record"
                : "Add Treatment History"}
            </button>
          </div>
        </form>
      </Modal>

      {/* DELETE CONFIRMATION MODAL */}
      <Modal
        title="Delete Record"
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
      >
        <p className="mb-4 text-gray-700">
          Are you sure you want to delete this treatment history record? This action cannot be undone.
        </p>

        <div className="flex justify-end space-x-3">
          <button className="btn-secondary" onClick={() => setIsDeleteOpen(false)}>
            Cancel
          </button>
          <button className="btn-danger" onClick={handleDelete}>
            Delete Record
          </button>
        </div>
      </Modal>

      {/* VIEW DETAILS MODAL */}
      <Modal
        title="Treatment History Record Details"
        isOpen={isDetailOpen && detailRecord !== null}
        onClose={() => {
          setIsDetailOpen(false);
          setDetailRecord(null);
        }}
        widthClassName="max-w-3xl"
      >
        {detailRecord && (
          <div className="space-y-5">
            {/* Header info */}
            <div className="flex flex-wrap justify-between items-center bg-gray-50 p-3.5 rounded-lg border border-gray-200 gap-2 text-sm">
              {detailRecord.event_date && (
                <span className="flex items-center font-medium text-gray-700">
                  <Calendar className="h-4 w-4 mr-1 text-gray-400" />
                  Event Date: <strong className="ml-1 text-gray-900">{detailRecord.event_date}</strong>
                </span>
              )}
              <span className="text-xs text-gray-500">
                Created: {new Date(detailRecord.createdAt).toLocaleString()}
              </span>
            </div>

            {/* Grid for core fields */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm bg-white p-4 rounded-lg border border-gray-100">
              <div>
                <span className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                  Timing of Relapse
                </span>
                <span className="font-semibold text-gray-900">{detailRecord.timing_of_relapse || "-"}</span>
              </div>

              <div>
                <span className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                  Disease Status
                </span>
                <span className="font-semibold text-gray-900">{detailRecord.disease_status || "-"}</span>
              </div>

              <div>
                <span className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                  Performance Status
                </span>
                <span className="font-semibold text-gray-900">{detailRecord.performance_status || "-"}</span>
              </div>

              <div>
                <span className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                  Previous Occurrence
                </span>
                <span className="text-gray-900">{detailRecord.previous_occurrence || "-"}</span>
              </div>
            </div>

            {/* Multiline Fields */}
            <div className="space-y-4 text-sm">
              {detailRecord.comorbidities && (
                <div>
                  <span className="font-semibold text-gray-900 block mb-1">Comorbidities:</span>
                  <div className="bg-gray-50 p-3 rounded-lg border border-gray-200 text-gray-800 whitespace-pre-wrap">
                    {detailRecord.comorbidities}
                  </div>
                </div>
              )}

              {detailRecord.treatment_progression && (
                <div>
                  <span className="font-semibold text-gray-900 block mb-1">Treatment Progression / Context:</span>
                  <div className="bg-gray-50 p-3 rounded-lg border border-gray-200 text-gray-800 whitespace-pre-wrap">
                    {detailRecord.treatment_progression}
                  </div>
                </div>
              )}

              {detailRecord.comments && (
                <div>
                  <span className="font-semibold text-gray-900 block mb-1">Comments:</span>
                  <div className="bg-gray-50 p-3 rounded-lg border border-gray-200 text-gray-800 whitespace-pre-wrap">
                    {detailRecord.comments}
                  </div>
                </div>
              )}
            </div>

            {/* Supporting Document Attachment */}
            {detailRecord.document_url && (
              <div className="flex flex-col sm:flex-row sm:items-center justify-between bg-primary-50/60 p-3.5 rounded-lg border border-primary-100 gap-2">
                <div className="flex items-center space-x-2 overflow-hidden">
                  <Paperclip className="h-4 w-4 text-primary-600 flex-shrink-0" />
                  <span className="text-sm font-medium text-gray-900 truncate">
                    {detailRecord.document_name || "Supporting Document"}
                  </span>
                  {detailRecord.document_size && (
                    <span className="text-xs text-gray-500 flex-shrink-0">
                      ({formatFileSize(detailRecord.document_size)})
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => handleViewDoc(detailRecord.id)}
                  className="btn-secondary text-xs flex items-center justify-center space-x-1 py-1.5 px-3"
                >
                  <span>View Document</span>
                  <ExternalLink className="h-3 w-3 ml-1" />
                </button>
              </div>
            )}

            {/* Audit Metadata */}
            <div className="pt-3 border-t border-gray-200 flex flex-col sm:flex-row justify-between text-xs text-gray-500 gap-1">
              <div>
                Created By: <strong className="text-gray-700">{detailRecord.creator?.name || "System/Doctor"}</strong> on{" "}
                {new Date(detailRecord.createdAt).toLocaleString()}
              </div>
              {detailRecord.updatedAt && detailRecord.updatedAt !== detailRecord.createdAt && (
                <div>
                  Updated By: <strong className="text-gray-700">{detailRecord.updater?.name || detailRecord.creator?.name || "User"}</strong> on{" "}
                  {new Date(detailRecord.updatedAt).toLocaleString()}
                </div>
              )}
            </div>

            <div className="flex justify-end pt-3 border-t border-gray-200">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  setIsDetailOpen(false);
                  setDetailRecord(null);
                }}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default TreatmentHistoryTab;
